/**
 * The routeInputs view allows to manage many of search boxes.
 *
 * The tomtom.routeInputs() method creates a new instance of RouteInputs view.
 *
 * @module Widgets
 * @class routeInputs
 *
 */
define('widgets/controls/routeInputs',['display/map', 'widgets/controls/searchBox', 'services/utils'], function(L, searchBox, utils) {
    'use strict';

    var EVENTS = {
            LocationsFound: 'locationsFound',
            LocationsCleared: 'locationsCleared',
            LocationError: 'locationError'
        }, LOCATION = {
            Start: 'start',
            End: 'end'
        }, WAYPOINT_PLACEMENT = {
            Last: 'last',
            BeforeLast: 'beforeLast'
        };
    var RouteInputs, routeInputConstructor;

    function getLocationsFromSearchBoxes() {
        var i;
        var locations = [];
        for (i = 0; i < this.searchBoxes.length; i += 1) {
            locations.push(this.searchBoxes[i].selectedLocation);
        }
        return locations;
    }

    function updateSearchBoxIcons() {
        var i, style;
        for (i = 0; i < this.searchBoxes.length; i += 1) {
            style = this.getIconClassName(i);
            this.searchBoxes[i].setIcon(style);
        }
    }

    function updateLocationButton() {
        var i, locationIconIndex;
        for (i = 0; i < this.searchBoxes.length; i += 1) {
            this.searchBoxes[i].removeLocationIcon();
        }
        locationIconIndex = this.options.location === LOCATION.Start ? 0 : this.searchBoxes.length - 1;
        this.searchBoxes[locationIconIndex].addLocationIcon();
    }

    function showDeleteSearchBoxButton(searchBox) {
        if (searchBox.deleteIcon === undefined) {
            searchBox.deleteIcon = new L.DomUtil.create('div', 'icon-trash-black leaflet-control-routeInputs-remove');
            searchBox._container.insertBefore(searchBox.deleteIcon, searchBox.resultsContainer);
            L.DomEvent.on(searchBox.deleteIcon, 'click', function() {
                this.removeInput(searchBox);
            }, this);
        }
    }

    function hideDeleteSearchBoxButton(searchBox) {
        if (searchBox.deleteIcon !== undefined) {
            searchBox.deleteIcon.parentNode.removeChild(searchBox.deleteIcon);
            searchBox.deleteIcon = undefined;
        }
    }

    function setVisibilityForDeleteButton(callback) {
        var i;
        for (i = 0; i < this.searchBoxes.length; i += 1) {
            callback.call(this, this.searchBoxes[i]);
        }
    }

    function updateDeleteSearchBoxButtons() {
        if (this.options.enableSearchBoxesModifcation === true) {
            if (this.searchBoxes.length === this.options.searchBoxes) {
                setVisibilityForDeleteButton.call(this, hideDeleteSearchBoxButton);
            } else {
                setVisibilityForDeleteButton.call(this, showDeleteSearchBoxButton);
            }
        }
    }

    function showInsertSearchBoxButton(container) {
        if (this.insertSearchBoxButton === undefined) {
            this.insertSearchBoxButton = new L.DomUtil.create('div', 'icon-add-black leaflet-control-routeInputs-add', container || this.getContainer());
            L.DomEvent.on(this.insertSearchBoxButton, 'click', this.addInput, this);
        }
    }

    function hideInsertSearchBoxButton() {
        if (this.insertSearchBoxButton !== undefined) {
            L.DomEvent.off(this.insertSearchBoxButton, 'click', this.addInput);
            this.insertSearchBoxButton.parentNode.removeChild(this.insertSearchBoxButton);
            this.insertSearchBoxButton = undefined;
        }
    }

    function updateInsertSearchBoxButton() {
        var i;
        if (this.options.enableSearchBoxesModifcation === true) {
            for (i = 0; i < this.searchBoxes.length; i += 1) {
                if (this.searchBoxes[i].input.value.length === 0) {
                    hideInsertSearchBoxButton.call(this);
                    return;
                }
            }
            showInsertSearchBoxButton.call(this);
        }
    }

    /**
     * The method to instantiate the routeInputs control.
     *
     * @method routeInputs
     * @param {Object} [options] Options to be passed to the routeInputs control.
     * @param {Number} [options.searchBoxes] Number of searchBoxes to be created.
     * @param {String} [options.position] Position of the control on a map.
     * @param {String} [options.location] Definition of place of creation location control. Might be equal: "start" or
     *                  "end".
     * @param {String} [options.enableSearchBoxesModifcation=false] Determines whether control should expose
     *     functionality of adding or removing waypoints by internal controls.
     * @param {String} [options.waypointPlacement="beforeLast"] Determines the place where new waypoint will added to.
     *                  There are two options: _"beforeLast"_ that adds waypoint before the last one and _"last"_ that
     *                  puts new waypoint at the end of the container.
     * @param {Object} [options.searchOptions] Options passed to the search service.
     *
     * @example
     * ```javascript
     *
     *  // Route inputs with two search boxes and location control
     *  var options = {
     *       searchBoxes: 2,
     *       location: routeInputs.Location.Start
     *   };
     *
     * var routeInputsInstance = routeInputs(options).addTo(map);
     *
     * ```
     */

    RouteInputs = L.Control.extend({

        includes: L.Mixin.Events,

        options: {
            searchBoxes: 2,
            position: 'topleft',
            location: LOCATION.Start,
            enableSearchBoxesModifcation: false,
            waypointPlacement: WAYPOINT_PLACEMENT.BeforeLast,
            searchOptions: {
                collapsible: false
            }
        },
        initialize: function(options) {
            if (options !== undefined && options.searchOptions !== undefined) {
                utils.addFields(options.searchOptions, this.options.searchOptions);
                delete options.searchOptions;
            }
            L.Util.setOptions(this, options);
            this.searchBoxes = [];
        },
        onAdd: function() {
            var i;
            var container = this.createContainer();
            this.searchBoxesContainer = new L.DomUtil.create('div', 'leaflet-control-search-boxes', container);
            for (i = 0; i < this.options.searchBoxes; i += 1) {
                this.searchBoxes.push(this.createSearchBox(i));
            }
            return container;
        },
        /**
         * Method used to create container for search boxes.
         *
         * @method createContainer
         *
         * @return {Node} Container of search boxes.
         */
        createContainer: function() {
            var container = new L.DomUtil.create('div', 'leaflet-control-routeInputs');
            L.DomEvent.disableClickPropagation(container);
            L.DomEvent.disableScrollPropagation(container);
            return container;
        },

        /**
         * Method used to get search box icon's class name, depending on its position (index).
         * Extracted class name will be added to the input.
         *
         * @method getIconClassName
         *
         * @return {String} Class name that determines icon look by setting _background-image_ property.
         */
        getIconClassName: function(index) {
            var className = 'icon-flag-black',
                lastSearchBoxIndex = this.options.searchBoxes > this.searchBoxes.length ? this.options.searchBoxes - 1 : this.searchBoxes.length - 1;
            if (index === 0) {
                className = 'icon-start-black';
            } else if (index === lastSearchBoxIndex) {
                className = 'icon-end-black';
            }
            return className;
        },
        /**
         * Method used to initialize search box by adding element to the container and applying event handlers on
         * it.
         *
         * @method createSearchBox
         * @param {Number} index Index of the search box (starting from 0).
         *
         * @return {Object} Modified search box object.
         */
        createSearchBox: function(index) {
            var searchBoxInstance;
            var options = utils.clone(this.options.searchOptions);
            options.location = this.needsLocationIcon(index);
            options.icon = this.getIconClassName(index);
            searchBoxInstance = searchBox(options).addTo(this._map);

            this.searchBoxesContainer.insertBefore(searchBoxInstance.getContainer(), this.searchBoxesContainer.childNodes[index]);
            this.addSearchBoxEventHandlers(searchBoxInstance);
            return searchBoxInstance;
        },
        /**
         * Method used to remove given search box from the view by specific index.
         *
         * @method removeSearchBox
         * @param {Number} index Index of the search box (starting from 0).
         *
         * @return {Object} Modified search box object.
         */
        removeSearchBox: function(index) {
            var searchBox = this.searchBoxesContainer.childNodes[index];
            searchBox.parentNode.removeChild(searchBox);
            return searchBox;
        },
        /**
         * Method used to check if search box needs location element.
         *
         * @method needsLocationIcon
         * @param {Number} searchBoxIndex Index of the given search box (starting from 0).
         *
         * @return {Boolean} Returns information of presence of location element.
         */
        needsLocationIcon: function(searchBoxIndex) {
            var first = this.options.location === LOCATION.Start && searchBoxIndex === 0,
                last = this.options.location === LOCATION.End && searchBoxIndex === this.options.searchBoxes - 1;
            return first || last;
        },
        /**
         * Method used to add handlers for given search box. If obtaining user's current location fails,
         * <a href="#events_LocationError">Event.LocationError</a> is fired.
         *
         * @method addSearchBoxEventHandlers
         * @param {Object} searchBox Search box instance used to add handlers on it.
         */
        addSearchBoxEventHandlers: function(searchBox) {
            var that = this;
            searchBox.on(searchBox.Event.ResultClicked, function(eventObject) {
                that.onLocationChange(eventObject);
            });
            searchBox.on(searchBox.Event.ClearButtonClicked, function(eventObject) {
                that.onLocationChange(eventObject);
                updateInsertSearchBoxButton.call(that);
            });
            searchBox.on(searchBox.Event.SearchInputChanged, function() {
                updateInsertSearchBoxButton.call(that);
            });
            if (searchBox.getLocation()) {
                searchBox.getLocation().on(searchBox.getLocation().Event.LocationError, function(eventObject) {
                    that.fire(EVENTS.LocationError, eventObject);
                });
            }
        },
        /**
         * Method used to manage changing state of search boxes. Depending on the content of the searchBox, there might
         * be fired two events: <a href="#events_LocationsFound">Event.LocationsFound</a> or <a
         * href="#events_LocationsCleared">Event.LocationsCleared</a>.
         *
         * @method onLocationChange
         * @param {Object} eventObject Object that contains event's information: instance of search box and
         *     position chosen within it
         */
        onLocationChange: function(eventObject) {
            var searchBox = eventObject.target;
            searchBox.selectedLocation = eventObject.data ? eventObject.data.position : undefined;

            this.fire(eventObject.data ? EVENTS.LocationsFound : EVENTS.LocationsCleared, {
                points: getLocationsFromSearchBoxes.call(this)
            });
        },
        /**
         * Method used to show notification when looking for current location fails.
         *
         * @method showLocationNotFoundMessageBox
         */
        showLocationNotFoundMessageBox: function() {
            L.messageBox({closeAfter: 1500}).openOn(this._map).setContent('Could not find your location');
        },

        /**
         * Method used to add searchBox to the specific position (index).
         *
         * @method addInput
         * @param {Number} [index] Represents position where searchBox should be added to. Not passing any index will
         *                          cause adding a searchBox at the end of list or before the last searchBox.
         *
         * @example
         * ```javascript
         *
         * var routeInputInstance = routeInputs().addTo(map);
         * routeInputInstance.addInput(0);
         * routeInputInstance.addInput();
         * ```
         */
        addInput: function(index) {
            if (index === undefined || index.target) {
                index = this.searchBoxes.length;
                if (this.options.waypointPlacement === WAYPOINT_PLACEMENT.BeforeLast) {
                    index -= 1;
                }
            }
            this.searchBoxes.splice(index, 0, this.createSearchBox(index));
            updateSearchBoxIcons.call(this);
            updateLocationButton.call(this);
            updateDeleteSearchBoxButtons.call(this);
            updateInsertSearchBoxButton.call(this)
        },

        /**
         * Method used to remove searchBox from the specific position (index).
         *
         * @method removeInput
         * @param {Number|Object} searchBox Represents index of the searchBox to be deleted or the element itself.
         *
         * @example
         * ```javascript
         *
         * var routeInputInstance = routeInputs().addTo(map);
         * routeInputInstance.removeInput(0);
         * routeInputInstance.removeInput();
         * ```
         */
        removeInput: function(searchBox) {
            var nodeList;
            if (searchBox === undefined) {
                searchBox = this.searchBoxes.length - 1;
            } else if (typeof searchBox === 'object') {
                nodeList = Array.prototype.slice.call(this.searchBoxesContainer.children);
                searchBox = nodeList.indexOf(searchBox.getContainer());
            }
            this.removeSearchBox(searchBox);
            this.searchBoxes.splice(searchBox, 1);
            updateSearchBoxIcons.call(this);
            updateLocationButton.call(this);
            updateDeleteSearchBoxButtons.call(this);
            updateInsertSearchBoxButton.call(this);

            this.fire(EVENTS.LocationsCleared, {points: getLocationsFromSearchBoxes.call(this)});
        },

        /**
         * Event that is fired when locations in all searchBoxes are changing.
         *
         * @event LocationsFound
         * @param {Array} points Array of points that represents locations from all searchBoxes, even if they are
         *     undefined.
         * @param {String} type Event's name, equal to "locationsFound".
         * @param {Element} target Event's target.
         *
         * @example
         * ```javascript
         *
         * var routeInputInstance = tomtom.routeInputs().addTo(map);
         * routeInputInstance.on(routeInputInstance.Event.LocationsFound, function (eventObject) {
         *      console.log(eventObject.points);
         * });
         * ```
         */
        /**
         * Event that is fired when locations in all searchBoxes are being removed.
         *
         * @event LocationsCleared
         * @param {Array} points Array of points that represents locations from all searchBoxes, even if they are
         *     undefined.
         * @param {String} type Event's name, equal to "locationsCleared".
         * @param {Element} target Event's target.
         *
         * @example
         * ```javascript
         *
         * var routeInputInstance = tomtom.routeInputs().addTo(map);
         * routeInputInstance.on(routeInputInstance.Event.LocationsCleared, function (eventObject) {
         *      console.log(eventObject.points);
         * });
         * ```
         */
        /**
         * Event that is fired when obtaining user's current location fails.
         *
         * @event LocationError
         * @param {Object} eventObject All failure information.
         * @param {String} type Event's name, equal to "locationError".
         * @param {Element} target Event's target.
         *
         * @example
         * ```javascript
         *
         * var routeInputInstance = tomtom.routeInputs().addTo(map);
         * routeInputInstance.on(routeInputInstance.Event.LocationError, function (eventObject) {
         *      console.log(eventObject);
         * });
         * ```
         */
        Event: EVENTS
    });

    routeInputConstructor = function(options) {
        return new RouteInputs(options);
    };
    /**
     * Determines the position that location icon will be added to.
     * Possible options are:
     * * _Location.Start_ - location will be added to the first searchBox in the list,
     * * _Location.End_ - location will be added to the last searchBox in the list.
     *
     * @property Location
     * @type {Object}
     *
     * @example
     * ```javascript
     *
     * var options = {location: tomtom.routeInputs.Location.Start};
     * var routeInputInstance = tomtom.routeInputs(options).addTo(map);
     * ```
     */
    routeInputConstructor.Location = LOCATION;

    /**
     * Determines the position where waypoint will be added to.
     * Possible options are:
     * * _WaypointPlacement.BeforeLast_ - new waypoint will be added before the last searchBox,
     * * _WaypointPlacement.Last_ - new waypoint will be added as the last element in the list.
     *
     * @property WaypointPlacement
     * @type {Object}
     *
     * @example
     * ```javascript
     *
     * var options = {waypointPlacement: tomtom.routeInputs.WaypointPlacement.BeforeLast};
     * var routeInputInstance = tomtom.routeInputs(options).addTo(map);
     * ```
     */
    routeInputConstructor.WaypointPlacement = WAYPOINT_PLACEMENT;

    return routeInputConstructor;
});

